import React, { useContext, useEffect, useMemo, useState } from "react";
import webcredAiBlack from "../../Assets/WebCredAI_BLACK.svg";
import webcredAiWhite from "../../Assets/webcredai_white.svg";
import { UserContext } from "../../App";
import { Link } from "react-router-dom";
import { fetchGetDataNoAuth } from "../../Utilities/FetchUtils";

const PROBLEM_LINES = [
  "Broken links.",
  "Missing tags.",
  "UX suggestions.",
  "But you still do not know:",
  "What actually matters?",
  "What will move rankings?",
  "What will improve conversions?",
  "So nothing gets fixed.",
];

const REPORT_POINTS = [
  "The 5 fixes that will move your metrics",
  "Exactly what to change (not just what's wrong)",
  "Where you're losing traffic or conversions",
  "What to fix first - and why",
];

const CLARITY_POINTS = [
  "What is hurting your site",
  "What to fix first",
  "What will actually drive results",
];

const COMPARISON_ROWS = [
  ["100+ issues", "Tells you what to fix first"],
  ["Technical-only tools", "SEO + UX + conversion"],
  ["Data-heavy reports", "Action-focused insights"],
  ["You decide what matters", "We tell you what matters"],
];

const AUDIENCES = [
  {
    title: "Founder",
    copy: "Launch faster with clear priorities",
  },
  {
    title: "Agencies",
    copy: "Deliver better reports in minutes",
  },
  {
    title: "Teams",
    copy: "Focus on what actually moves metrics",
  },
];

const STEPS = [
  {
    title: "Create your account",
    copy: "Get 10 free credits instantly",
  },
  {
    title: "Audit any page",
    copy: "Run analysis in seconds",
  },
  {
    title: "Get your action plan",
    copy: "Fix what matters first",
  },
];

const PREVIEW_TOP_BLOCKERS = [
  "Weak headline messaging",
  "Missing trust signals",
  "Slow mobile performance",
];

const PREVIEW_SCORE = 69;

const PREVIEW_PRIORITY_QUEUE = [
  {
    title: "Clarify the homepage headline",
    impact: "High impact",
    effort: "10 min",
  },
  {
    title: "Move trust cues above the fold",
    impact: "High impact",
    effort: "15 min",
  },
  {
    title: "Reduce mobile weight on the landing page",
    impact: "Medium impact",
    effort: "30 min",
  },
];

const PREVIEW_QUICK_WINS = [
  "Fix CTA visibility",
  "Tighten headline copy",
  "Reduce hero image weight",
];

const PREVIEW_SECTION_SCORES = [
  { label: "SEO", score: 78 },
  { label: "UX", score: 66 },
  { label: "Conversion", score: 71 },
  { label: "Performance", score: 63 },
];

const FALLBACK_PRICING = {
  IN: [
    { sku: "starter_25", name: "Starter", credits: 25, price_amount: 49900, currency: "INR" },
    { sku: "growth_100", name: "Growth", credits: 100, price_amount: 149900, currency: "INR" },
    { sku: "agency_300", name: "Agency", credits: 300, price_amount: 299900, currency: "INR" },
  ],
  US: [
    { sku: "starter_10_usd", name: "Starter", credits: 10, price_amount: 1900, currency: "USD" },
    { sku: "growth_100_usd", name: "Growth", credits: 100, price_amount: 15000, currency: "USD" },
    { sku: "agency_300_usd", name: "Agency", credits: 300, price_amount: 30000, currency: "USD" },
  ],
};

const TRUST_POINTS = [
  "Used by founders and marketers working on real websites",
  "Built with real SEO and product experience",
  "Focused on clarity, not noise",
];

const TRUST_OUTCOMES = [
  {
    title: "Founder outcome",
    copy: "Finally see what actually matters.",
  },
  {
    title: "Marketer outcome",
    copy: "Save hours of figuring out what to fix.",
  },
];

const FAQS = [
  {
    question: "Do credits expire?",
    answer:
      "Credits do not expire during the current launch period. If that changes later, it will be shown clearly before purchase.",
  },
  {
    question: "Can I scan a full site?",
    answer:
      "Yes. Site discovery is free. When you choose pages to analyze, each analyzed page uses 1 credit.",
  },
  {
    question: "How is this different?",
    answer:
      "Most tools show data. WebCredAI shows what to do. It turns long audit lists into a shorter action plan ranked by what matters first.",
  },
  {
    question: "Do I need technical knowledge?",
    answer:
      "No. The report is written for founders, marketers, agencies, and teams that need clear next steps.",
  },
];

function getPrimaryHref(isUserLoggedIn) {
  return isUserLoggedIn ? "/dashboard" : "/signup";
}

function getFallbackPricingRegion() {
  if (typeof window === "undefined") return "US";
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  if (timeZone === "Asia/Kolkata" || timeZone === "Asia/Calcutta") return "IN";
  const locale = window.navigator?.language || "";
  return locale.toUpperCase().endsWith("-IN") ? "IN" : "US";
}

function formatCurrencyMinorUnits(amount, currency = "INR") {
  const locale = currency === "INR" ? "en-IN" : "en-US";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format((amount || 0) / 100);
  } catch (error) {
    return `${currency} ${((amount || 0) / 100).toFixed(0)}`;
  }
}

function getInitialTheme() {
  if (typeof window === "undefined") return "dark";
  const saved = window.localStorage.getItem("wc-theme");
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia?.("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

export default function LandingPage() {
  const { isUserLoggedIn } = useContext(UserContext);
  const [theme] = useState(getInitialTheme);
  const [scrolled, setScrolled] = useState(false);
  const [pricingPacks, setPricingPacks] = useState(
    () => FALLBACK_PRICING[getFallbackPricingRegion()]
  );
  const isLight = theme === "light";
  const primaryHref = getPrimaryHref(isUserLoggedIn);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 18);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("light", isLight);
  }, [isLight]);

  useEffect(() => {
    let isActive = true;
    fetchGetDataNoAuth("payments/pricing").then(({ response }) => {
      if (isActive && Array.isArray(response) && response.length > 0) {
        setPricingPacks(response);
      }
    });
    return () => {
      isActive = false;
    };
  }, []);

  const tokens = useMemo(
    () => ({
      bg: isLight ? "#FFFFFF" : "#020617",
      text: isLight ? "#0F172A" : "#E2E8F0",
      secondary: isLight ? "#475569" : "#94A3B8",
      accent: isLight ? "#2563EB" : "#3B82F6",
      surface: isLight ? "#F8FAFC" : "#0F172A",
      surfaceStrong: isLight ? "#FFFFFF" : "#111827",
      border: isLight ? "rgba(15, 23, 42, 0.12)" : "rgba(226, 232, 240, 0.14)",
      mutedBorder: isLight
        ? "rgba(15, 23, 42, 0.08)"
        : "rgba(226, 232, 240, 0.08)",
      glow: isLight
        ? "rgba(37, 99, 235, 0.16)"
        : "rgba(59, 130, 246, 0.22)",
      shadow: isLight
        ? "0 24px 80px rgba(15, 23, 42, 0.10)"
        : "0 24px 80px rgba(0, 0, 0, 0.42)",
    }),
    [isLight]
  );

  return (
    <main
      className="landing-page"
      data-theme={theme}
      style={{
        "--bg": tokens.bg,
        "--text": tokens.text,
        "--secondary": tokens.secondary,
        "--accent": tokens.accent,
        "--surface": tokens.surface,
        "--surface-strong": tokens.surfaceStrong,
        "--border": tokens.border,
        "--muted-border": tokens.mutedBorder,
        "--glow": tokens.glow,
        "--shadow": tokens.shadow,
      }}
    >
      <Header
        isLight={isLight}
        isLoggedIn={Boolean(isUserLoggedIn)}
        primaryHref={primaryHref}
        scrolled={scrolled}
      />

      <section
        id="hero"
        className="landing-section hero-section"
        aria-labelledby="hero-title"
      >
        <div className="landing-container hero-grid">
          <div className="hero-copy">
            <p className="section-kicker">The problem</p>
            <h1 id="hero-title">
              <span>You run audits.</span>
              <span>You get 100+ issues.</span>
              <span className="hero-title-outcome">Don't know what to fix first.</span>
            </h1>
            <p>
              WebCredAI turns audit noise into a clear, prioritized action plan
              across SEO, UX, and conversion.
            </p>
            <div className="hero-actions">
              <Link to={primaryHref} className="button button-primary">
                Start with free credits
              </Link>
              <a href="#how" className="button button-secondary">
                See how it works
              </a>
            </div>
            <p className="hero-reassurance">
              No credit card needed. 10 credits included.
            </p>
          </div>

          <div className="hero-proof">
            <ProductPreview
              label="Real WebCredAI dashboard"
              heightClass="hero-shot"
              variant="hero"
              loading="eager"
            />
          </div>
        </div>
      </section>

      <section className="landing-section trust-section" aria-labelledby="trust-title">
        <div className="landing-container trust-grid">
          <div>
            <p className="section-kicker">Trust</p>
            <h2 id="trust-title">Built for real-world decisions</h2>
            <p className="section-subcopy">
              WebCredAI is for people who need the next fix to be obvious,
              not buried inside another dense audit export.
            </p>
          </div>
          <div className="trust-panel">
            <div className="trust-points">
              {TRUST_POINTS.map((point) => (
                <p key={point}>{point}</p>
              ))}
            </div>
            <div className="testimonial-grid">
              {TRUST_OUTCOMES.map((item) => (
                <article className="testimonial-card" key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className="landing-section deep-problem-section"
        aria-labelledby="deep-problem-title"
      >
        <div className="landing-container deep-problem-wrap">
          <p className="section-kicker">The deeper problem</p>
          <h2 id="deep-problem-title" className="deep-problem-copy">
            {PROBLEM_LINES.map((line, index) => (
              <span
                className={`deep-problem-line ${
                  index >= 4 && index <= 6 ? "deep-problem-question" : ""
                } ${index === PROBLEM_LINES.length - 1 ? "deep-problem-final" : ""}`}
                key={line}
                style={{ animationDelay: `${index * 80}ms` }}
              >
                {line}
              </span>
            ))}
          </h2>
        </div>
      </section>

      <section
        id="solution"
        className="landing-section bridge-section"
        aria-labelledby="bridge-title"
      >
        <div className="landing-container narrow">
          <p className="section-kicker">The bridge</p>
          <h2 id="bridge-title">
            That is exactly why we built WebCredAI.
          </h2>
          <p className="bridge-copy">
            WebCredAI does not give you more data.
          </p>
          <p className="bridge-copy strong">
            It tells you what to fix first.
          </p>
          <Link to={primaryHref} className="primary-link">
            Start with free credits
          </Link>
        </div>
      </section>

      <section className="landing-section report-section" aria-labelledby="report-title">
        <div className="landing-container">
          <div className="section-heading centered">
            <p className="section-kicker">What you get</p>
            <h2 id="report-title">This is what you'll actually act on</h2>
          </div>

          <div className="report-demo-card">
            <ProductPreview
              label="Annotated WebCredAI report"
              heightClass="report-shot"
              variant="report"
            />
          </div>

          <div className="report-points">
            {REPORT_POINTS.map((point) => (
              <div key={point} className="report-point">
                <span aria-hidden="true">+</span>
                {point}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section clarity-strip" aria-labelledby="clarity-title">
        <div className="landing-container">
          <h2 id="clarity-title">In under 5 minutes, you'll know:</h2>
          <div className="clarity-list">
            {CLARITY_POINTS.map((point) => (
              <p key={point}>{point}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section" aria-labelledby="different-title">
        <div className="landing-container comparison-grid">
          <div>
            <p className="section-kicker">Different by design</p>
            <h2 id="different-title">Why WebCredAI is different</h2>
            <p className="section-subcopy">
              It is built for decisions. Not dashboards that leave the hard
              prioritization work to you.
            </p>
            <Link to={primaryHref} className="primary-link">
              Start with free credits
            </Link>
          </div>

          <div className="comparison-card" role="table" aria-label="Comparison">
            <div className="comparison-row comparison-head" role="row">
              <span role="columnheader">Others</span>
              <span role="columnheader">WebCredAI</span>
            </div>
            {COMPARISON_ROWS.map(([others, webcred]) => (
              <div className="comparison-row" role="row" key={others}>
                <span role="cell">{others}</span>
                <span role="cell">{webcred}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section" aria-labelledby="audience-title">
        <div className="landing-container">
          <div className="section-heading">
            <p className="section-kicker">Who it is for</p>
            <h2 id="audience-title">Built for teams that need clarity</h2>
          </div>
          <div className="three-card-grid">
            {AUDIENCES.map((item) => (
              <article className="simple-card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="how" className="landing-section" aria-labelledby="how-title">
        <div className="landing-container how-grid">
          <div>
            <p className="section-kicker">How it works</p>
            <h2 id="how-title">Simple, fast, and focused</h2>
            <p className="section-subcopy">
              The workflow stays short so you can move from audit to action.
            </p>
          </div>
          <div className="step-list">
            {STEPS.map((step, index) => (
              <article className="step-card" key={step.title}>
                <span>{index + 1}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.copy}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        id="pricing"
        className="landing-section pricing-section"
        aria-labelledby="pricing-title"
      >
        <div className="landing-container pricing-card">
          <div>
            <p className="section-kicker">Pricing</p>
            <h2 id="pricing-title">Simple credit-based pricing</h2>
            <p className="section-subcopy">
              1 credit = 1 analyzed page. Site discovery is free.
            </p>
          </div>
          <div className="pricing-guidance">
            <p>Most startups use 25-50 credits/month.</p>
            <div className="pricing-pack-row" aria-label="Credit pack prices">
              {pricingPacks.map((pack) => (
                <div className="pricing-pack" key={pack.sku}>
                  <span>{pack.credits} credits</span>
                  <strong>{formatCurrencyMinorUnits(pack.price_amount, pack.currency)}</strong>
                </div>
              ))}
            </div>
            <Link to={primaryHref} className="button button-primary">
              Start with free credits
            </Link>
          </div>
        </div>
      </section>

      <section id="faq" className="landing-section" aria-labelledby="faq-title">
        <div className="landing-container faq-grid">
          <div>
            <p className="section-kicker">FAQ</p>
            <h2 id="faq-title">Questions before you start</h2>
          </div>
          <div className="faq-list">
            {FAQS.map((item) => (
              <article className="faq-card" key={item.question}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section final-cta" aria-labelledby="final-title">
        <div className="landing-container centered">
          <h2 id="final-title">
            Start free.
            <br />
            Know what to fix.
            <br />
            Then scale when you're ready.
          </h2>
          <Link to={primaryHref} className="button button-primary">
            Get 10 free credits
          </Link>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-container footer-grid">
          <Link to="/" className="footer-brand" aria-label="WebCredAI Home">
            <img src={isLight ? webcredAiBlack : webcredAiWhite} alt="WebCredAI logo" />
            <span>WebCredAI</span>
          </Link>
          <nav aria-label="Footer">
            <a href="#hero">Product</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/refund-policy">Refunds</Link>
            <a href="mailto:support@webcredai.com">Contact</a>
          </nav>
        </div>
      </footer>

      <style>{landingStyles}</style>
    </main>
  );
}

function Header({ isLight, isLoggedIn, primaryHref, scrolled }) {
  return (
    <header className={`landing-header ${scrolled ? "is-scrolled" : ""}`}>
      <div className="landing-container header-inner">
        <Link to="/" className="brand" aria-label="WebCredAI Home">
          <img src={isLight ? webcredAiBlack : webcredAiWhite} alt="WebCredAI logo" />
          <span>WebCredAI</span>
        </Link>
        <nav aria-label="Primary">
          <Link to={isLoggedIn ? "/dashboard" : "/login"} className="login-link">
            {isLoggedIn ? "Dashboard" : "Login"}
          </Link>
          <Link to={primaryHref} className="button button-primary header-cta">
            Start free
          </Link>
        </nav>
      </div>
    </header>
  );
}

function ProductPreview({ label, heightClass, variant = "hero" }) {
  const showExpanded = variant === "report";
  return (
    <figure className={`product-shot ${heightClass}`}>
      <div className="shot-glow" aria-hidden="true" />
      <div className="shot-frame">
        <div className="shot-bar">
          <span />
          <span />
          <span />
          <p>{label}</p>
        </div>
        <div className={`preview-canvas preview-${variant}`}>
          <div className="preview-summary-card">
            <div className="preview-summary-copy">
              <span className="preview-eyebrow">What to fix first</span>
              <h3>Your site is losing conversions due to 3 clear blockers.</h3>
              <ul className="preview-blocker-list">
                {PREVIEW_TOP_BLOCKERS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="preview-score-card">
              <div className="preview-score-ring">
                <div className="preview-score-ring-inner">
                  <strong>{PREVIEW_SCORE}</strong>
                  <span>Example</span>
                </div>
              </div>
              <div className="preview-score-meta">
                <span className="preview-pill">Priority score</span>
                <p>Consultant-style action plan across SEO, UX, and conversion.</p>
              </div>
            </div>
          </div>

          <div className="preview-content-grid">
            <article className="preview-panel preview-priority-panel">
              <div className="preview-panel-header">
                <div>
                  <span className="preview-panel-kicker">Level 1</span>
                  <h4>Highest priority fixes</h4>
                </div>
                <span className="preview-panel-tag">Top 3</span>
              </div>
              <div className="preview-priority-list">
                {PREVIEW_PRIORITY_QUEUE.map((item, index) => (
                  <div key={item.title} className="preview-priority-item">
                    <div className="preview-priority-rank">#{index + 1}</div>
                    <div className="preview-priority-copy">
                      <strong>{item.title}</strong>
                      <div className="preview-priority-meta">
                        <span>{item.impact}</span>
                        <span>{item.effort}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </article>

            <article className="preview-panel preview-quickwins-panel">
              <div className="preview-panel-header">
                <div>
                  <span className="preview-panel-kicker">Quick wins</span>
                  <h4>Can improve results today</h4>
                </div>
              </div>
              <div className="preview-quickwins-list">
                {PREVIEW_QUICK_WINS.map((item) => (
                  <div key={item} className="preview-quickwin-item">
                    <span className="preview-quickwin-dot" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </article>
          </div>

          {showExpanded && (
            <div className="preview-sections-row">
              {PREVIEW_SECTION_SCORES.map((item) => (
                <div key={item.label} className="preview-section-card">
                  <span>{item.label}</span>
                  <strong>{item.score}</strong>
                  <div className="preview-section-bar">
                    <div style={{ width: `${item.score}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </figure>
  );
}

const landingStyles = `
  .landing-page {
    min-height: 100vh;
    overflow-x: hidden;
    background:
      radial-gradient(circle at 80% -10%, var(--glow), transparent 34rem),
      radial-gradient(circle at 0% 24rem, color-mix(in srgb, var(--accent) 8%, transparent), transparent 30rem),
      var(--bg);
    color: var(--text);
    font-family: "Avenir Next", "Segoe UI", sans-serif;
    letter-spacing: -0.01em;
  }

  .landing-page * {
    box-sizing: border-box;
  }

  .landing-page a {
    color: inherit;
    text-decoration: none;
  }

  .landing-container {
    width: min(1200px, calc(100% - 40px));
    margin: 0 auto;
  }

  .landing-section {
    padding: 80px 0;
    border-top: 1px solid var(--muted-border);
  }

  .landing-section:first-of-type {
    border-top: 0;
  }

  .landing-header {
    position: sticky;
    top: 0;
    z-index: 30;
    background: color-mix(in srgb, var(--bg) 92%, transparent);
    border-bottom: 1px solid transparent;
    transition: opacity 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
  }

  .landing-header.is-scrolled {
    opacity: 0.94;
    border-bottom-color: var(--border);
    box-shadow: 0 8px 24px color-mix(in srgb, var(--text) 6%, transparent);
  }

  .header-inner,
  .landing-header nav,
  .brand,
  .footer-brand {
    display: flex;
    align-items: center;
  }

  .header-inner {
    min-height: 72px;
    justify-content: space-between;
    gap: 24px;
  }

  .brand,
  .footer-brand {
    gap: 12px;
    font-size: 18px;
    font-weight: 700;
  }

  .brand img,
  .footer-brand img {
    width: 34px;
    height: 34px;
    object-fit: contain;
  }

  .landing-header nav {
    gap: 16px;
  }

  .login-link {
    color: var(--secondary);
    font-weight: 600;
  }

  .button,
  .primary-link,
  .text-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    border-radius: 999px;
    font-weight: 700;
    transition:
      transform 160ms ease,
      border-color 160ms ease,
      background 160ms ease,
      box-shadow 160ms ease;
  }

  .button:hover,
  .primary-link:hover,
  .text-link:hover {
    box-shadow: 0 18px 42px color-mix(in srgb, var(--accent) 24%, transparent);
    transform: scale(1.02);
  }

  .button-primary {
    padding: 0 22px;
    color: #FFFFFF;
    background: var(--accent);
    box-shadow: 0 16px 36px color-mix(in srgb, var(--accent) 26%, transparent);
  }

  .button-secondary {
    padding: 0 20px;
    color: var(--text);
    border: 1px solid var(--border);
    background: color-mix(in srgb, var(--surface-strong) 80%, transparent);
  }

  .primary-link {
    margin-top: 28px;
    padding: 0 20px;
    color: #FFFFFF;
    background: var(--accent);
  }

  .text-link {
    min-height: auto;
    color: var(--accent);
    font-weight: 700;
  }

  .hero-section {
    display: flex;
    min-height: calc(100vh - 72px);
    align-items: center;
    padding: 64px 0;
    border-top: 0;
  }

  .problem-grid,
  .comparison-grid,
  .how-grid,
  .trust-grid,
  .faq-grid {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 32px;
    align-items: center;
  }

  .hero-grid {
    display: grid;
    grid-template-columns: minmax(0, 0.45fr) minmax(0, 0.55fr);
    gap: 54px;
    align-items: center;
  }

  .problem-grid > div:first-child,
  .comparison-grid > div:first-child,
  .how-grid > div:first-child,
  .trust-grid > div:first-child,
  .faq-grid > div:first-child {
    grid-column: span 5;
  }

  .problem-note,
  .comparison-card,
  .step-list,
  .trust-panel,
  .faq-list {
    grid-column: span 7;
  }

  .section-kicker {
    margin: 0 0 16px;
    color: var(--accent);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .hero-copy {
    max-width: 520px;
  }

  .hero-copy .section-kicker,
  .hero-copy h1,
  .hero-copy p,
  .hero-actions,
  .hero-reassurance {
    opacity: 0;
    transform: translateY(10px);
    animation: landingFadeUp 560ms ease forwards;
  }

  .hero-copy h1 {
    animation-delay: 90ms;
  }

  .hero-copy p {
    animation-delay: 180ms;
  }

  .hero-actions {
    animation-delay: 270ms;
  }

  .hero-reassurance {
    animation-delay: 340ms;
  }

  .hero-copy h1 {
    margin: 0;
    font-size: clamp(40px, 4.6vw, 58px);
    line-height: 1.12;
    font-weight: 700;
    letter-spacing: -0.04em;
  }

  .hero-copy h1 span {
    display: block;
  }

  .hero-title-outcome {
    margin-top: 14px;
  }

  .deep-problem-wrap {
    max-width: 700px;
  }

  .deep-problem-copy {
    margin: 0;
    font-size: clamp(30px, 4.2vw, 48px);
    line-height: 1.45;
    font-weight: 700;
    letter-spacing: -0.035em;
  }

  .deep-problem-line {
    display: block;
    opacity: 0;
    transform: translateY(10px);
    animation: landingFadeUp 520ms ease forwards;
  }

  .deep-problem-question {
    color: var(--secondary);
  }

  .deep-problem-final {
    margin-top: 24px;
    color: var(--accent);
  }

  .problem-note {
    align-self: end;
    max-width: 430px;
    margin-left: auto;
    padding: 28px;
    border: 1px solid var(--border);
    border-radius: 28px;
    background: color-mix(in srgb, var(--surface-strong) 80%, transparent);
    box-shadow: var(--shadow);
  }

  .problem-note span {
    display: block;
    font-size: 20px;
    font-weight: 700;
  }

  .problem-note p,
  .section-subcopy,
  .hero-copy p,
  .bridge-copy,
  .simple-card p,
  .step-card p,
  .faq-card p {
    color: var(--secondary);
    font-size: 16px;
    line-height: 1.65;
  }

  .narrow {
    max-width: 820px;
  }

  .bridge-section h2,
  .section-heading h2,
  .comparison-grid h2,
  .how-grid h2,
  .pricing-card h2,
  .trust-grid h2,
  .faq-grid h2,
  .final-cta h2,
  .clarity-strip h2 {
    margin: 0;
    font-size: clamp(32px, 4vw, 48px);
    line-height: 1.1;
    font-weight: 700;
    letter-spacing: -0.035em;
  }

  .bridge-copy {
    margin: 28px 0 0;
    font-size: clamp(22px, 3vw, 32px);
    line-height: 1.3;
  }

  .bridge-copy.strong {
    margin-top: 8px;
    color: var(--text);
    font-weight: 700;
  }

  .hero-copy p {
    max-width: 520px;
    margin: 16px 0 0;
    font-size: 18px;
    line-height: 1.58;
  }

  .hero-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 24px;
  }

  .hero-copy .hero-reassurance {
    margin: 14px 0 0;
    color: var(--secondary);
    font-size: 14px;
    font-weight: 600;
    line-height: 1.5;
  }

  .hero-proof {
    position: relative;
    min-width: 0;
  }

  .hero-proof .product-shot {
    transform-origin: center;
    animation: none;
  }

  .hero-proof .shot-frame {
    border-radius: 16px;
    box-shadow: 0 18px 48px color-mix(in srgb, var(--text) 10%, transparent);
  }

  .hero-proof .shot-glow {
    inset: -34px;
    border-radius: 32px;
    background:
      radial-gradient(circle at 30% 16%, color-mix(in srgb, var(--accent) 24%, transparent), transparent 52%),
      radial-gradient(circle at 86% 60%, color-mix(in srgb, var(--accent) 16%, transparent), transparent 56%);
  }

  .product-shot {
    position: relative;
    margin: 0;
  }

  .shot-glow {
    position: absolute;
    inset: -24px;
    z-index: 0;
    border-radius: 36px;
    background: radial-gradient(circle at 50% 15%, var(--glow), transparent 58%);
    opacity: 0.7;
  }

  .shot-frame {
    position: relative;
    z-index: 1;
    overflow: hidden;
    border: 1px solid var(--border);
    border-radius: 30px;
    background: #020617;
    box-shadow: var(--shadow);
  }

  .shot-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 42px;
    padding: 0 16px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.10);
    color: #94A3B8;
    font-size: 13px;
  }

  .shot-bar span {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #334155;
  }

  .shot-bar p {
    margin: 0 0 0 8px;
  }

  .preview-canvas {
    display: grid;
    gap: 16px;
    min-height: 100%;
    padding: 18px;
    background:
      radial-gradient(circle at 18% 0%, rgba(59, 130, 246, 0.16), transparent 28%),
      linear-gradient(180deg, rgba(15, 23, 42, 0.98), rgba(2, 6, 23, 1));
    color: #E2E8F0;
  }

  .preview-summary-card,
  .preview-content-grid,
  .preview-sections-row {
    display: grid;
    gap: 14px;
  }

  .preview-summary-card {
    grid-template-columns: minmax(0, 1.45fr) minmax(0, 0.9fr);
    align-items: stretch;
  }

  .preview-summary-copy,
  .preview-score-card,
  .preview-panel,
  .preview-section-card {
    border: 1px solid rgba(148, 163, 184, 0.16);
    border-radius: 20px;
    background: rgba(15, 23, 42, 0.78);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
  }

  .preview-summary-copy {
    padding: 20px;
  }

  .preview-eyebrow,
  .preview-panel-kicker {
    display: inline-block;
    color: #60A5FA;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .preview-summary-copy h3 {
    margin: 10px 0 0;
    font-size: clamp(20px, 2vw, 28px);
    line-height: 1.16;
    letter-spacing: -0.03em;
  }

  .preview-blocker-list {
    display: grid;
    gap: 10px;
    margin: 16px 0 0;
    padding: 0;
    list-style: none;
  }

  .preview-blocker-list li {
    position: relative;
    padding-left: 18px;
    color: #CBD5E1;
    font-size: 13px;
    line-height: 1.5;
  }

  .preview-blocker-list li::before {
    content: "";
    position: absolute;
    top: 8px;
    left: 0;
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #60A5FA;
    box-shadow: 0 0 14px rgba(96, 165, 250, 0.4);
  }

  .preview-score-card {
    display: grid;
    grid-template-rows: auto 1fr;
    justify-items: center;
    gap: 14px;
    min-height: 100%;
    padding: 18px;
    text-align: center;
  }

  .preview-score-ring {
    display: flex;
    width: 104px;
    height: 104px;
    margin: 0 auto;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background:
      radial-gradient(circle at center, rgba(15, 23, 42, 0.95) 0 56%, transparent 57%),
      conic-gradient(#60A5FA 0 69%, rgba(148, 163, 184, 0.18) 69% 100%);
  }

  .preview-score-ring-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 70px;
    height: 70px;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.92);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      0 0 0 1px rgba(96, 165, 250, 0.08);
  }

  .preview-score-ring strong {
    display: block;
    font-size: 26px;
    line-height: 0.95;
    font-variant-numeric: tabular-nums;
    transform: translateX(1px);
  }

  .preview-score-ring span {
    display: block;
    margin-top: 6px;
    color: #94A3B8;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .preview-score-meta {
    display: grid;
    gap: 10px;
    align-content: end;
    justify-items: center;
  }

  .preview-pill,
  .preview-panel-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 28px;
    padding: 0 10px;
    border: 1px solid rgba(96, 165, 250, 0.24);
    border-radius: 999px;
    background: rgba(37, 99, 235, 0.14);
    color: #BFDBFE;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .preview-score-meta p {
    margin: 0;
    max-width: 22ch;
    color: #CBD5E1;
    font-size: 12px;
    line-height: 1.5;
    text-wrap: balance;
  }

  .preview-content-grid {
    grid-template-columns: minmax(0, 1.25fr) minmax(0, 0.85fr);
  }

  .preview-panel {
    padding: 16px;
  }

  .preview-panel-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .preview-panel-header h4 {
    margin: 8px 0 0;
    font-size: 16px;
    line-height: 1.25;
    letter-spacing: -0.02em;
  }

  .preview-priority-list,
  .preview-quickwins-list {
    display: grid;
    gap: 10px;
    margin-top: 16px;
  }

  .preview-priority-item,
  .preview-quickwin-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 13px;
    border: 1px solid rgba(148, 163, 184, 0.12);
    border-radius: 16px;
    background: rgba(2, 6, 23, 0.5);
  }

  .preview-priority-rank {
    display: grid;
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    place-items: center;
    border-radius: 12px;
    background: rgba(37, 99, 235, 0.16);
    color: #BFDBFE;
    font-size: 12px;
    font-weight: 800;
  }

  .preview-priority-copy {
    min-width: 0;
  }

  .preview-priority-copy strong {
    display: block;
    color: #F8FAFC;
    font-size: 13px;
    line-height: 1.35;
  }

  .preview-priority-meta {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 8px;
  }

  .preview-priority-meta span {
    padding: 4px 8px;
    border-radius: 999px;
    background: rgba(148, 163, 184, 0.12);
    color: #94A3B8;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .preview-quickwin-dot {
    width: 9px;
    height: 9px;
    flex-shrink: 0;
    margin-top: 5px;
    border-radius: 999px;
    background: #34D399;
    box-shadow: 0 0 14px rgba(52, 211, 153, 0.4);
  }

  .preview-quickwin-item span:last-child {
    color: #CBD5E1;
    font-size: 13px;
    line-height: 1.45;
  }

  .preview-sections-row {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .preview-section-card {
    padding: 14px;
  }

  .preview-section-card span {
    display: block;
    color: #94A3B8;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .preview-section-card strong {
    display: block;
    margin-top: 8px;
    font-size: 24px;
    line-height: 1;
  }

  .preview-section-bar {
    height: 8px;
    margin-top: 12px;
    overflow: hidden;
    border-radius: 999px;
    background: rgba(148, 163, 184, 0.14);
  }

  .preview-section-bar div {
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #3B82F6, #60A5FA);
  }

  .hero-shot .shot-frame {
    height: clamp(360px, 46vw, 560px);
  }

  .report-shot .shot-frame {
    height: clamp(420px, 56vw, 680px);
  }

  .section-heading {
    max-width: 680px;
  }

  .centered {
    text-align: center;
  }

  .section-heading.centered {
    margin: 0 auto 36px;
  }

  .report-demo-card {
    position: relative;
    max-width: 1040px;
    margin: 0 auto;
  }

  .report-points {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    max-width: 1040px;
    margin: 24px auto 0;
  }

  .report-point {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 18px;
    border: 1px solid var(--border);
    border-radius: 18px;
    background: color-mix(in srgb, var(--surface-strong) 76%, transparent);
    color: var(--secondary);
    line-height: 1.45;
  }

  .report-point span {
    color: var(--accent);
    font-weight: 700;
  }

  .clarity-strip {
    text-align: center;
  }

  .clarity-strip h2 {
    font-size: clamp(36px, 4.6vw, 58px);
  }

  .clarity-list {
    display: flex;
    justify-content: center;
    gap: 34px;
    flex-wrap: wrap;
    margin-top: 36px;
  }

  .clarity-list p {
    margin: 0;
    color: var(--secondary);
    font-size: clamp(20px, 2.2vw, 28px);
    font-weight: 600;
  }

  .comparison-card,
  .pricing-card,
  .simple-card,
  .step-card,
  .faq-card,
  .report-point,
  .trust-panel,
  .testimonial-card {
    border: 1px solid var(--border);
    background: color-mix(in srgb, var(--surface-strong) 82%, transparent);
    box-shadow: var(--shadow);
    transition:
      transform 160ms ease,
      box-shadow 160ms ease,
      border-color 160ms ease;
  }

  .comparison-card:hover,
  .pricing-card:hover,
  .simple-card:hover,
  .step-card:hover,
  .faq-card:hover,
  .report-point:hover,
  .trust-panel:hover,
  .testimonial-card:hover {
    border-color: color-mix(in srgb, var(--accent) 28%, var(--border));
    box-shadow: 0 26px 70px color-mix(in srgb, var(--text) 12%, transparent);
    transform: translateY(-2px);
  }

  .comparison-card {
    overflow: hidden;
    border-radius: 28px;
  }

  .comparison-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .comparison-row span {
    padding: 20px 22px;
    border-top: 1px solid var(--muted-border);
    color: var(--secondary);
    line-height: 1.4;
  }

  .comparison-row span + span {
    color: var(--text);
    font-weight: 700;
    border-left: 1px solid var(--muted-border);
  }

  .comparison-head span {
    border-top: 0;
    color: var(--text);
    font-weight: 700;
  }

  .three-card-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
    margin-top: 32px;
  }

  .simple-card {
    min-height: 180px;
    padding: 28px;
    border-radius: 26px;
  }

  .simple-card h3,
  .step-card h3,
  .faq-card h3 {
    margin: 0;
    font-size: 20px;
    line-height: 1.25;
    font-weight: 700;
  }

  .simple-card p,
  .step-card p,
  .faq-card p {
    margin: 12px 0 0;
  }

  .step-list {
    display: grid;
    gap: 14px;
  }

  .step-card {
    display: grid;
    grid-template-columns: 54px 1fr;
    gap: 18px;
    padding: 22px;
    border-radius: 24px;
  }

  .step-card > span {
    display: grid;
    width: 44px;
    height: 44px;
    place-items: center;
    border-radius: 50%;
    background: color-mix(in srgb, var(--accent) 14%, transparent);
    color: var(--accent);
    font-weight: 800;
  }

  .pricing-card {
    display: grid;
    grid-template-columns: 1.2fr 0.8fr;
    gap: 28px;
    align-items: center;
    padding: 36px;
    border-radius: 30px;
  }

  .pricing-guidance {
    display: grid;
    justify-items: start;
    gap: 18px;
  }

  .pricing-guidance p {
    margin: 0;
    color: var(--text);
    font-size: 22px;
    font-weight: 700;
    line-height: 1.3;
  }

  .pricing-pack-row {
    display: grid;
    width: 100%;
    gap: 10px;
  }

  .pricing-pack {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 12px 14px;
    border: 1px solid var(--muted-border);
    border-radius: 16px;
    background: color-mix(in srgb, var(--surface) 64%, transparent);
  }

  .pricing-pack span {
    color: var(--secondary);
    font-size: 14px;
    font-weight: 600;
  }

  .pricing-pack strong {
    color: var(--text);
    font-size: 18px;
    font-weight: 800;
  }

  .faq-list {
    display: grid;
    gap: 14px;
  }

  .faq-card {
    padding: 24px;
    border-radius: 24px;
  }

  .final-cta h2 {
    font-size: clamp(40px, 6vw, 72px);
  }

  .final-cta .button {
    margin-top: 32px;
  }

  .landing-footer {
    padding: 32px 0;
    border-top: 1px solid var(--muted-border);
  }

  .footer-grid {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  }

  .landing-footer nav {
    display: flex;
    gap: 24px;
    color: var(--secondary);
    font-weight: 600;
  }

  @keyframes landingFadeUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes heroFloat {
    0%,
    100% {
      transform: scale(1.07) translateY(0);
    }

    50% {
      transform: scale(1.08) translateY(-8px);
    }
  }

  .trust-section {
    background: linear-gradient(
      180deg,
      color-mix(in srgb, var(--surface) 46%, transparent),
      transparent
    );
  }

  .trust-panel {
    display: grid;
    gap: 22px;
    padding: 26px;
    border-radius: 28px;
  }

  .trust-points {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
  }

  .trust-points p {
    position: relative;
    margin: 0;
    padding-left: 18px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--muted-border);
    color: var(--secondary);
    font-size: 15px;
    font-weight: 600;
    line-height: 1.5;
  }

  .trust-points p::before {
    content: "";
    position: absolute;
    top: 8px;
    left: 0;
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: var(--accent);
  }

  .testimonial-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
  }

  .testimonial-card {
    margin: 0;
    padding: 20px;
    border-radius: 20px;
    background:
      linear-gradient(
        135deg,
        color-mix(in srgb, var(--accent) 9%, transparent),
        color-mix(in srgb, var(--surface-strong) 86%, transparent)
      );
  }

  .testimonial-card h3 {
    margin: 0;
    color: var(--accent);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .testimonial-card p {
    margin: 10px 0 0;
    color: var(--text);
    font-size: 18px;
    font-weight: 700;
    line-height: 1.35;
  }

  @media (max-width: 900px) {
    .landing-section {
      padding: 40px 0;
    }

    .landing-container {
      width: min(100% - 32px, 1200px);
    }

    .header-inner {
      min-height: 66px;
    }

    .brand span {
      display: none;
    }

    .landing-header nav {
      gap: 10px;
    }

    .login-link {
      display: none;
    }

    .header-cta {
      min-height: 40px;
      padding: 0 16px;
      font-size: 14px;
    }

    .problem-grid,
    .hero-grid,
    .comparison-grid,
    .how-grid,
    .trust-grid,
    .faq-grid,
    .pricing-card {
      display: block;
    }

    .hero-section {
      display: block;
      min-height: auto;
      padding: 34px 0 42px;
    }

    .section-kicker {
      margin-bottom: 10px;
      font-size: 12px;
    }

    .hero-copy {
      max-width: none;
    }

    .hero-copy h1 {
      font-size: clamp(34px, 10.5vw, 44px);
      line-height: 1.06;
    }

    .hero-title-outcome {
      margin-top: 10px;
    }

    .hero-copy p {
      margin-top: 14px;
      font-size: 16px;
      line-height: 1.55;
    }

    .hero-actions {
      display: grid;
      gap: 10px;
      margin-top: 20px;
      width: 100%;
    }

    .hero-actions .button,
    .pricing-guidance .button {
      width: 100%;
      min-height: 46px;
    }

    .hero-copy .hero-reassurance {
      margin-top: 10px;
      text-align: center;
      font-size: 13px;
    }

    .problem-note,
    .hero-proof,
    .comparison-card,
    .step-list,
    .trust-panel,
    .faq-list,
    .pricing-guidance {
      margin-top: 20px;
    }

    .hero-proof {
      display: block;
      margin-top: 20px;
    }

    .hero-proof .product-shot {
      animation: none;
      transform: none;
      margin: 0;
    }

    .shot-glow {
      inset: -12px;
      filter: blur(10px);
    }

    .shot-frame {
      border-radius: 18px;
    }

    .preview-canvas {
      gap: 12px;
      padding: 14px;
    }

    .preview-summary-card,
    .preview-content-grid,
    .preview-sections-row {
      grid-template-columns: 1fr;
    }

    .preview-score-card {
      grid-template-columns: auto 1fr;
      align-items: center;
    }

    .preview-score-ring {
      width: 88px;
      height: 88px;
    }

    .preview-score-ring-inner {
      width: 58px;
      height: 58px;
    }

    .preview-score-ring strong {
      font-size: 22px;
    }

    .preview-summary-copy,
    .preview-panel,
    .preview-section-card {
      border-radius: 18px;
    }

    .preview-summary-copy h3 {
      font-size: 18px;
    }

    .preview-panel-header h4 {
      font-size: 15px;
    }

    .shot-bar {
      min-height: 34px;
      padding: 0 12px;
      font-size: 11px;
    }

    .shot-bar span {
      width: 7px;
      height: 7px;
    }

    .hero-shot .shot-frame {
      height: clamp(260px, 72vw, 340px);
    }

    .report-shot .shot-frame {
      height: clamp(340px, 92vw, 460px);
    }

    .deep-problem-copy {
      font-size: clamp(28px, 8.6vw, 40px);
      line-height: 1.34;
    }

    .report-points,
    .three-card-grid {
      grid-template-columns: 1fr;
    }

    .clarity-list {
      display: grid;
      gap: 16px;
    }

    .clarity-list p {
      font-size: clamp(20px, 6vw, 26px);
      line-height: 1.25;
    }

    .trust-points,
    .testimonial-grid {
      grid-template-columns: 1fr;
    }

    .trust-panel {
      padding: 18px;
      border-radius: 22px;
    }

    .trust-points p {
      padding-bottom: 14px;
    }

    .testimonial-card {
      padding: 18px;
    }

    .report-points {
      gap: 10px;
      margin-top: 18px;
    }

    .report-point {
      padding: 16px;
    }

    .comparison-row span {
      padding: 16px;
    }

    .footer-grid,
    .landing-footer nav {
      align-items: flex-start;
      flex-direction: column;
    }
  }

  @media (max-width: 640px) {
    .landing-section {
      padding: 32px 0;
    }

    .landing-container {
      width: min(100% - 24px, 1200px);
    }

    .header-inner {
      min-height: 62px;
      gap: 12px;
    }

    .landing-header nav {
      gap: 8px;
    }

    .header-cta {
      min-height: 38px;
      padding: 0 14px;
      font-size: 13px;
    }

    .hero-section {
      padding: 22px 0 34px;
    }

    .hero-copy h1 {
      font-size: clamp(30px, 10.2vw, 38px);
      line-height: 1.04;
      letter-spacing: -0.05em;
    }

    .hero-copy p {
      font-size: 15px;
      line-height: 1.55;
      max-width: none;
    }

    .hero-actions {
      margin-top: 16px;
    }

    .hero-proof {
      margin-top: 18px;
    }

    .shot-frame {
      border-radius: 16px;
    }

    .hero-shot .shot-frame {
      height: clamp(210px, 72vw, 292px);
    }

    .report-shot .shot-frame {
      height: clamp(260px, 94vw, 380px);
    }

    .preview-canvas {
      padding: 12px;
      gap: 10px;
    }

    .preview-score-card {
      grid-template-columns: 1fr;
      justify-items: center;
    }

    .preview-score-ring {
      width: 78px;
      height: 78px;
    }

    .preview-score-ring-inner {
      width: 52px;
      height: 52px;
    }

    .preview-score-ring strong {
      font-size: 20px;
    }

    .preview-summary-copy,
    .preview-panel,
    .preview-section-card {
      padding: 14px;
      border-radius: 16px;
    }

    .preview-summary-copy h3 {
      font-size: 16px;
    }

    .preview-blocker-list {
      gap: 8px;
      margin-top: 12px;
    }

    .preview-blocker-list li,
    .preview-quickwin-item span:last-child,
    .preview-priority-copy strong {
      font-size: 12px;
    }

    .preview-priority-item,
    .preview-quickwin-item {
      gap: 10px;
      padding: 10px 11px;
      border-radius: 14px;
    }

    .preview-priority-rank {
      width: 30px;
      height: 30px;
      border-radius: 10px;
      font-size: 11px;
    }

    .preview-priority-meta {
      margin-top: 6px;
    }

    .preview-priority-meta span,
    .preview-pill,
    .preview-panel-tag {
      font-size: 9px;
    }

    .preview-section-card strong {
      font-size: 20px;
    }

    .bridge-section h2,
    .section-heading h2,
    .comparison-grid h2,
    .how-grid h2,
    .pricing-card h2,
    .trust-grid h2,
    .faq-grid h2,
    .clarity-strip h2 {
      font-size: clamp(28px, 8.2vw, 34px);
    }

    .final-cta h2 {
      font-size: clamp(32px, 10vw, 44px);
    }

    .section-heading.centered {
      margin-bottom: 18px;
    }

    .deep-problem-copy {
      font-size: clamp(22px, 8vw, 32px);
      line-height: 1.38;
    }

    .deep-problem-final {
      margin-top: 18px;
    }

    .trust-panel,
    .simple-card,
    .step-card,
    .faq-card {
      padding: 16px;
      border-radius: 20px;
    }

    .simple-card {
      min-height: auto;
    }

    .step-card {
      grid-template-columns: 46px 1fr;
      gap: 14px;
    }

    .step-card > span {
      width: 40px;
      height: 40px;
    }

    .testimonial-card {
      padding: 16px;
    }

    .testimonial-card p {
      font-size: 16px;
    }

    .comparison-row span {
      padding: 13px 12px;
      font-size: 13px;
    }

    .pricing-card {
      gap: 18px;
      padding: 22px 16px;
      border-radius: 22px;
    }

    .pricing-guidance {
      gap: 14px;
    }

    .pricing-guidance p {
      font-size: 18px;
    }

    .pricing-pack {
      align-items: flex-start;
      flex-direction: column;
      gap: 6px;
    }

    .pricing-pack strong {
      font-size: 17px;
    }

    .clarity-list {
      margin-top: 24px;
      gap: 10px;
    }

    .clarity-list p {
      font-size: clamp(18px, 5.8vw, 24px);
    }

    .landing-footer nav {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      width: 100%;
      gap: 12px 16px;
    }

    .final-cta .button {
      width: 100%;
      margin-top: 24px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .deep-problem-line {
      opacity: 1;
      transform: none;
      animation: none;
    }

    .hero-copy .section-kicker,
    .hero-copy h1,
    .hero-copy p,
    .hero-actions,
    .hero-reassurance,
    .hero-proof .product-shot {
      opacity: 1;
      transform: none;
      animation: none;
    }

    .button,
    .primary-link,
    .text-link {
      transition: none;
    }
  }
`;
